import types from '../mutation-types';
import VoipDispositionConfigAPI from '../../api/voipDispositionConfig';
import CallRecordsAPI from '../../api/callRecords';
import VoipPendingDispositionsAPI from '../../api/voipPendingDispositions';

// Store pro ciclo de disposition pós-chamada (Phase 6).
//
// Phase 2.9.10 Commit 1: modal desacoplado do CallRecord. modalContext
// substitui modalCallRecord — sempre carrega ambos callRecord (nullable)
// e pendingCallEnd (dados do SDK). Form e header caem em fallback
// gracioso quando CallRecord ainda nao chegou.
//
// Responsabilidades:
// - Manter `config` (opções, campos ativos, trigger_on) fresh
// - Manter lista de `pending` (CallRecords sem disposition preenchida)
// - Controlar visibilidade do modal via `modalContext`
//
// Modal é acionado por useCallEndDetection ao detectar fim de chamada;
// store só cuida de estado + persistência.

export const state = {
  config: null,
  pending: [],
  modalContext: null, // { callRecord: obj|null, pendingCallEnd: obj|null }
  uiFlags: {
    isFetchingConfig: false,
    isSavingConfig: false,
    isFetchingPending: false,
    isSavingDisposition: false,
  },
};

// Spec 2026-04-23: options virou array de hashes
// { name, funnel_id, funnel_stage }. Helper extrai so os names pras
// partes da UI que soh querem a lista de strings (dropdown do modal,
// selecao em presets, etc).
function optionEntryName(option) {
  if (typeof option === 'string') return option;
  if (option && typeof option === 'object') return option.name || '';
  return '';
}

export const getters = {
  getConfig:        _state => _state.config,
  getPending:       _state => _state.pending,
  getPendingCount:  _state => _state.pending.length,
  getModalContext:  _state => _state.modalContext,
  // Phase 2.9.10 Commit 1 back-compat: callers antigos pegam CallRecord
  // direto ou null. Novos callers devem usar getModalContext pra ter
  // acesso ao pendingCallEnd tambem.
  getModalCallRecord: _state => _state.modalContext?.callRecord || null,
  getUIFlags:       _state => _state.uiFlags,
  // Spec 2026-04-23: extrai nomes das opcoes (novo shape: array de hashes).
  // Back-compat pra config carregada antes do refresh: se vier array de
  // strings (shape antigo em cache), passa direto.
  getOptionNames: _state => {
    const opts = _state.config?.options || [];
    return opts.map(optionEntryName).filter(Boolean);
  },
  // Resolve helper pro frontend: valor ainda ativo vs soft-removed.
  // Compara contra nomes das opcoes (novo shape).
  isOptionActive:   (_state, _getters) => value => {
    if (!_state.config) return true;
    return _getters.getOptionNames.includes(value);
  },
};

export const actions = {
  async fetchConfig({ commit }) {
    commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isFetchingConfig: true });
    try {
      const { data } = await VoipDispositionConfigAPI.show();
      commit(types.VOIP_DISPOSITION_SET_CONFIG, data);
      return data;
    } finally {
      commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isFetchingConfig: false });
    }
  },

  async updateConfig({ commit }, payload) {
    commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isSavingConfig: true });
    try {
      const { data } = await VoipDispositionConfigAPI.update(payload);
      commit(types.VOIP_DISPOSITION_SET_CONFIG, data);
      return data;
    } finally {
      commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isSavingConfig: false });
    }
  },

  async fetchPending({ commit }) {
    commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isFetchingPending: true });
    try {
      const { data } = await CallRecordsAPI.listPending();
      // Index endpoint agora retorna { meta, data: [...] } — extrai array
      const records = Array.isArray(data) ? data : (data?.data || []);
      commit(types.VOIP_DISPOSITION_SET_PENDING, records);
      return records;
    } finally {
      commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isFetchingPending: false });
    }
  },

  async saveDisposition({ commit }, { callRecordId, payload }) {
    commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isSavingDisposition: true });
    try {
      const { data } = await CallRecordsAPI.saveDisposition(callRecordId, payload);
      // Assume que resposta preencheu disposition_filled_at → remove do pending
      commit(types.VOIP_DISPOSITION_REMOVE_PENDING, callRecordId);
      commit(types.VOIP_DISPOSITION_HIDE_MODAL);
      return data;
    } finally {
      commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isSavingDisposition: false });
    }
  },

  // Phase 2.9.10 Commit 1: salva disposition com fallback pra pending.
  // Aceita contexto completo (pendingCallEnd + callRecord opcional) e o
  // backend decide se grava direto no CallRecord ou guarda em
  // voip_pending_dispositions. Resposta vem com `kind` indicando rota.
  async saveDispositionFromModal({ commit }, { pendingCallEnd, callRecord, payload }) {
    commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isSavingDisposition: true });
    try {
      // Merge dados do SDK + payload do form. Backend usa SDK fields quando
      // vai criar VoipPendingDisposition; DispositionSaveService ignora eles
      // se CallRecord ja existe.
      const body = {
        ...payload,
        provider_call_id:
          pendingCallEnd?.provider_call_id || callRecord?.provider_call_id,
        peer_phone: pendingCallEnd?.peer_phone,
        peer_name: pendingCallEnd?.peer_name,
        direction: pendingCallEnd?.direction,
        final_status: pendingCallEnd?.final_status,
        duration_seconds: pendingCallEnd?.duration_seconds,
      };
      const { data } = await VoipPendingDispositionsAPI.save(body);

      // Se backend confirmou CallRecord real, remove do pending list
      if (data.kind === 'call_record' && data.call_record?.id) {
        commit(types.VOIP_DISPOSITION_REMOVE_PENDING, data.call_record.id);
      }
      commit(types.VOIP_DISPOSITION_HIDE_MODAL);
      return data;
    } finally {
      commit(types.VOIP_DISPOSITION_SET_UI_FLAG, { isSavingDisposition: false });
    }
  },

  // Phase 2.9.10 Commit 1: showModal aceita { callRecord, pendingCallEnd }.
  // Qualquer dos dois pode ser null — mas pelo menos um deve existir.
  // Back-compat: se caller antigo passa callRecord diretamente (obj com id),
  // embrulha no novo shape.
  showModal({ commit }, input) {
    let ctx;
    if (input?.callRecord || input?.pendingCallEnd) {
      ctx = {
        callRecord:     input.callRecord || null,
        pendingCallEnd: input.pendingCallEnd || null,
      };
    } else if (input?.id || input?.provider_call_id) {
      // caller antigo — passou CallRecord direto
      ctx = { callRecord: input, pendingCallEnd: null };
    } else {
      return;
    }

    if (ctx.callRecord) {
      commit(types.VOIP_DISPOSITION_UPSERT_PENDING, ctx.callRecord);
    }
    commit(types.VOIP_DISPOSITION_SHOW_MODAL, ctx);
  },

  hideModal({ commit }) {
    commit(types.VOIP_DISPOSITION_HIDE_MODAL);
  },
};

export const mutations = {
  [types.VOIP_DISPOSITION_SET_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  [types.VOIP_DISPOSITION_SET_CONFIG](_state, config) {
    _state.config = config;
  },
  [types.VOIP_DISPOSITION_SET_PENDING](_state, records) {
    _state.pending = records;
  },
  [types.VOIP_DISPOSITION_UPSERT_PENDING](_state, record) {
    const idx = _state.pending.findIndex(r => r.id === record.id);
    if (idx === -1) {
      _state.pending = [record, ..._state.pending];
    } else {
      _state.pending.splice(idx, 1, record);
    }
  },
  [types.VOIP_DISPOSITION_REMOVE_PENDING](_state, id) {
    _state.pending = _state.pending.filter(r => r.id !== id);
  },
  [types.VOIP_DISPOSITION_SHOW_MODAL](_state, ctx) {
    _state.modalContext = ctx;
  },
  [types.VOIP_DISPOSITION_HIDE_MODAL](_state) {
    _state.modalContext = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
